import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Lightning,
  ShieldCheck,
  ChatCircle,
  Handshake,
  Certificate,
  Eye,
  Users,
  Trophy,
  Target
} from '@phosphor-icons/react';
import { PrimarySectionBackdrop } from '../components/PrimarySectionBackdrop';
import { Seo } from '../components/Seo';
import { KONTAKT_FORM_PATH } from '../constants/navigation';
import { SEO } from '../constants/seoCopy';

const base = process.env.PUBLIC_URL || '';

/** Tri slike kao na početnoj hero mreži (bez Viessmann / prsluka) */
const ABOUT_HERO_IMAGES = {
  vaillant: `${base}/hero/hero-vaillant-montaza.png`,
  radionica: `${base}/hero/hero-radionica.png`,
  ventilacija: `${base}/hero/hero-ventilacija-kanali.png`,
};

const values = [
  { icon: Lightning, title: 'Brzina', desc: 'Brza reakcija i dolazak na vašu adresu kada vam je najpotrebnije.' },
  { icon: ShieldCheck, title: 'Garancija', desc: 'Garantujemo kvalitet svakog posla koji obavimo.' },
  { icon: ChatCircle, title: 'Komunikacija', desc: 'Jasna, direktna i transparentna komunikacija.' },
  { icon: Eye, title: 'Transparentnost', desc: 'Bez skrivenih troškova, sve cene su unapred poznate.' },
  { icon: Certificate, title: 'Stručnost', desc: 'Profesionalni saveti zasnovani na iskustvu.' },
  { icon: Handshake, title: 'Posvećenost', desc: 'Od prvog kontakta do završetka posla, tu smo za vas.' },
];

const stats = [
  { icon: Users, number: '100+', label: 'Klijenata' },
  { icon: Trophy, number: '150+', label: 'Projekata' },
  { icon: Target, number: '100%', label: 'Posvećenost' },
];

const processSteps = [
  { step: '01', title: 'Kontakt', desc: 'Javite nam se telefonom ili porukom' },
  { step: '02', title: 'Procena', desc: 'Besplatna analiza vaših potreba' },
  { step: '03', title: 'Ponuda', desc: 'Transparentna cena bez skrivenih troškova' },
  { step: '04', title: 'Realizacija', desc: 'Profesionalna ugradnja i servis' },
];

export default function ONamaPage() {
  return (
    <div data-testid="o-nama-page" className="pt-20">
      <Seo {...SEO.oNama} />
      {/* Hero */}
      <section className="relative py-24 bg-primary overflow-hidden" data-testid="o-nama-header">
        <PrimarySectionBackdrop />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <span className="inline-block text-cool font-bold uppercase tracking-[0.2em] text-sm mb-4">
              O nama
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6">
              THERMO COOL SYSTEM
            </h1>
            <p className="text-white/70 text-lg">
              Vaš pouzdan partner za klimatizaciju, grejanje i ventilaciju od 2022. godine.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 bg-white" data-testid="about-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-accent/10 px-4 py-2 rounded-full mb-6">
                <span className="text-accent font-semibold text-sm">Osnovano 5. 8. 2022.</span>
              </div>
              
              <h2 className="font-heading text-3xl sm:text-4xl font-black text-primary mb-6">
                Energija koja štedi,<br />komfor koji traje
              </h2>
              
              <div className="space-y-4 text-muted leading-relaxed">
                <p>
                  THERMO COOL SYSTEM je preduzeće specijalizovano za savremena, energetski efikasna 
                  rešenja klimatizacije, grejanja i ventilacije za sve tipove objekata - stambene, 
                  poslovne i industrijske.
                </p>
                <p>
                  Od osnivanja 2022. godine, posvećeni smo pružanju vrhunske usluge našim klijentima. 
                  Naš tim stručnjaka kombinuje znanje i iskustvo kako bi vam omogućio optimalan 
                  komfor u vašem prostoru, uz maksimalnu energetsku efikasnost.
                </p>
                <p>
                  Verujemo da svaki klijent zaslužuje personalizovan pristup i rešenje koje je 
                  prilagođeno njegovim specifičnim potrebama i budžetu.
                </p>
              </div>

              {/* Mini Stats */}
              <div className="grid grid-cols-3 gap-6 mt-10">
                {stats.map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="w-12 h-12 bg-surface rounded-xl flex items-center justify-center mx-auto mb-3">
                      <stat.icon size={24} weight="duotone" className="text-accent" />
                    </div>
                    <p className="font-heading text-2xl font-black text-primary">{stat.number}</p>
                    <p className="text-muted text-sm">{stat.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Images */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
              data-testid="about-hero-images"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="rounded-3xl overflow-hidden shadow-lg">
                    <img
                      src={ABOUT_HERO_IMAGES.vaillant}
                      alt="Montaža spoljašnjih klima uređaja na gradilištu"
                      className="w-full h-48 object-cover"
                    />
                  </div>
                  <div className="rounded-3xl overflow-hidden shadow-lg">
                    <img
                      src={ABOUT_HERO_IMAGES.radionica}
                      alt="Zavarivanje metalnih cevi u radionici"
                      className="w-full h-48 object-cover"
                    />
                  </div>
                </div>
                <div className="pt-8">
                  <div className="rounded-3xl overflow-hidden shadow-lg h-full min-h-[280px] sm:min-h-[320px]">
                    <img
                      src={ABOUT_HERO_IMAGES.ventilacija}
                      alt="Ugradnja ventilacionih kanala na plafonu"
                      className="w-full h-full min-h-[280px] sm:min-h-[320px] object-cover"
                    />
                  </div>
                </div>
              </div>

              {/* Badge */}
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-primary text-white px-6 py-3 rounded-full shadow-xl">
                <span className="font-bold">Since 2022</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-surface" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block text-heat font-bold uppercase tracking-[0.2em] text-sm mb-4">
              Naše vrednosti
            </span>
            <h2 className="font-heading text-4xl font-black text-primary">
              Zašto nas klijenti biraju
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, i) => (
              <motion.div
                key={i}
                className="bg-white p-8 rounded-2xl shadow-lg card-hover"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`value-card-${i}`}
              >
                <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center mb-6">
                  <value.icon size={28} weight="duotone" className="text-accent" />
                </div>
                <h3 className="font-heading text-xl font-bold text-primary mb-3">{value.title}</h3>
                <p className="text-muted">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Kako radimo — Naš proces */}
      <section className="py-24 bg-white" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block text-accent font-bold uppercase tracking-[0.2em] text-sm mb-4">
              Kako radimo
            </span>
            <h2 className="font-heading text-4xl font-black text-primary">
              Naš proces
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((item, i) => (
              <motion.div
                key={i}
                className="relative bg-surface p-8 rounded-2xl shadow-lg border border-primary/5"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`process-step-${i + 1}`}
              >
                <span className="font-heading text-6xl font-black text-accent/10">{item.step}</span>
                <h3 className="font-heading text-xl font-bold text-primary mt-2 mb-2">{item.title}</h3>
                <p className="text-muted">{item.desc}</p>
                {i < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-accent/20" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="relative overflow-hidden py-24 bg-primary" data-testid="mission-section">
        <PrimarySectionBackdrop />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-black text-white mb-8">
              Naša misija
            </h2>
            <p className="text-white/80 text-xl leading-relaxed">
              "Pružiti svim našim klijentima vrhunska HVAC rešenja koja kombinuju 
              modernu tehnologiju, energetsku efikasnost i dugotrajnost, uz profesionalnu 
              uslugu i podršku koja nadmašuje očekivanja."
            </p>
            <div className="mt-10 flex justify-center gap-6">
              <div className="text-center">
                <p className="font-heading text-4xl font-black text-cool">3+</p>
                <p className="text-white/60 text-sm">Godine iskustva</p>
              </div>
              <div className="w-px bg-white/20" />
              <div className="text-center">
                <p className="font-heading text-4xl font-black text-heat">100+</p>
                <p className="text-white/60 text-sm">Zadovoljnih klijenata</p>
              </div>
              <div className="w-px bg-white/20" />
              <div className="text-center">
                <p className="font-heading text-4xl font-black text-mint">150+</p>
                <p className="text-white/60 text-sm">Projekata</p>
              </div>
            </div>

            <div className="mt-12">
              <Link
                to={KONTAKT_FORM_PATH}
                className="inline-flex items-center justify-center gap-2 sm:gap-3 bg-mint hover:bg-mint/90 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 shadow-lg shadow-black/20"
                data-testid="o-nama-mission-cta"
              >
                Javite nam se
                <ArrowRight size={22} weight="bold" aria-hidden />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
